import { Button, Cell, ProgressBar } from '@zeiss/beyond-online-react';
import { Box, ButtonGroup, Grid } from '@zeiss/lib-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import {
  ApplicationListApplication,
  getApplicationsChanges,
  OutletContextType,
  UpdateApplicationsModal,
} from './';
import { useApplicationListApplications } from 'src/hooks/api';
import { ApplicationCard } from 'src/modules/TenantManagement/components/ApplicationCard';
import { ApplicationSearch } from 'src/modules/TenantManagement/components/ApplicationSearch';
import { Application } from 'src/types/tenantManagement';

// @todo: Add empty state: design + code
export const Applications = () => {
  const { t } = useTranslation(['common']);
  const { applicationListId, applicationListName, canUpdateApplications } =
    useOutletContext<OutletContextType>();

  const [apps, setApps] = React.useState<ApplicationListApplication[]>([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    React.useState(false);

  const {
    data: applications,
    error: errorApplications,
    isPending: isPendingApplications,
  } = useApplicationListApplications(applicationListId);

  React.useEffect(() => {
    if (applications) {
      const appListApplications: ApplicationListApplication[] =
        applications.items.map(({ id, name, status, zeissDepartment }) => ({
          id,
          isMarkedForDeletion: false,
          name,
          status,
          type: 'existing',
          zeissDepartment,
        }));

      setApps(appListApplications);
    }
  }, [applications]);

  if (isPendingApplications) {
    return <ProgressBar animate="spinning" />;
  }

  if (errorApplications) {
    return <div>Error loading data</div>;
  }

  const existingApps: ApplicationListApplication[] = applications.items.map(
    ({ id, name, status, zeissDepartment }) => ({
      id,
      isMarkedForDeletion: false,
      name,
      status,
      type: 'existing',
      zeissDepartment,
    }),
  );

  const cancel = () => {
    setApps(existingApps);
  };

  const showConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const onSelectApplication = (application: Application) => {
    setApps((prev) => [
      ...prev,
      { ...application, isMarkedForDeletion: false, type: 'new' },
    ]);
  };

  const onDismiss = (id: string) => {
    const isExistingApplication =
      existingApps.findIndex((item) => item.id === id) > -1;

    let updatedApplications: ApplicationListApplication[];

    if (isExistingApplication) {
      updatedApplications = apps.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            isMarkedForDeletion: true,
          };
        }

        return item;
      });
    } else {
      updatedApplications = apps.filter((item) => item.id !== id);
    }

    setApps(updatedApplications);
  };

  const onRestore = (id: string) => {
    const updatedApplications = apps.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          isMarkedForDeletion: false,
        };
      }

      return item;
    });

    setApps(updatedApplications);
  };

  const changes = getApplicationsChanges({
    current: apps,
    initial: existingApps,
  });

  const hasUnsavedChanges = Boolean(
    changes.addedApplications.length || changes.removedApplications.length,
  );

  return (
    <>
      {canUpdateApplications ? (
        <Box>
          <Grid>
            <Cell.Basic colSpan={[4, 6, 5]}>
              <ApplicationSearch
                label={t('common:action.addX', {
                  x: t('common:glossary.application_other'),
                })}
                onSelectApplication={onSelectApplication}
                selectedApplications={apps}
              />
            </Cell.Basic>
          </Grid>
        </Box>
      ) : null}

      {apps.length ? (
        <Box>
          <Grid>
            {apps.map(
              ({ id, isMarkedForDeletion, name, type, zeissDepartment }) => (
                <Cell.Basic colSpan={[4, 6, 5, 4]} key={id}>
                  <ApplicationCard
                    confirmDismiss
                    department={zeissDepartment}
                    id={id}
                    name={name}
                    onDismiss={onDismiss}
                    onRestore={onRestore}
                    readonly={canUpdateApplications ? false : true}
                    status={
                      type === 'new'
                        ? 'adding'
                        : isMarkedForDeletion
                          ? 'removing'
                          : undefined
                    }
                  />
                </Cell.Basic>
              ),
            )}
          </Grid>
        </Box>
      ) : null}

      {hasUnsavedChanges ? (
        <ButtonGroup spacing="s" variant="form">
          <Button
            disabled={!hasUnsavedChanges}
            onClick={cancel}
            variant="secondary"
          >
            {t('common:action.cancel')}
          </Button>

          <Button disabled={!hasUnsavedChanges} onClick={showConfirmationModal}>
            {t('common:action.saveChanges')}
          </Button>
        </ButtonGroup>
      ) : null}

      <UpdateApplicationsModal
        data={{ name: applicationListName, ...changes }}
        isOpen={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
      />
    </>
  );
};
